import React, { useEffect, useRef, useState } from 'react';
import {
  CircleCheck, Copy, Check, ExternalLink,
  ChevronRight, Webhook, RefreshCw, Plug,
  ShoppingBag, Link2Off, Eye, EyeOff, Link2, Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { merchantCredentialsAPI, shopifyConfigAPI } from '../../services/api';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:          '#ffffff',
  bgSub:       '#ffffff',
  border:      '#e8e8f1',
  borderSoft:  '#f3f4f6',
  text:        '#14142b',
  textSub:     '#6e6e86',
  textMuted:   '#9ca3af',
  green:       '#16a34a',
  greenBg:     '#f0fdf4',
  greenBorder: '#bbf7d0',
  red:         '#dc2626',
  redBg:       '#fef2f2',
  redBorder:   '#fecaca',
  yellow:      '#d97706',
  yellowBg:    '#fffbeb',
  yellowBorder:'#fde68a',
  blue:        '#7c3aed',
  blueBg:      '#f1ebff',
  blueBorder:  '#bfdbfe',
  codebg: '#f4f4f9',
  codeDark:    '#14142b',
};

// ─── Primitives ───────────────────────────────────────────────────────────────
const useClipboard = () => {
  const [copied, setCopied] = useState(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  return { copy, copied };
};

const CopyBtn = ({ text, id, copied, copy }) => (
  <button
    onClick={() => copy(text, id)}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 6,
      border: '1px solid rgba(255,255,255,.15)',
      background: copied === id ? 'rgba(255,255,255,.15)' : 'rgba(255,255,255,.08)',
      color: copied === id ? '#86efac' : '#6e6e86',
      fontSize: 11, fontWeight: 600, cursor: 'pointer',
    }}
  >
    {copied === id ? <Check size={11} /> : <Copy size={11} />}
    {copied === id ? 'Copied' : 'Copy'}
  </button>
);

const CodeBlock = ({ code, lang, label, copied, copy }) => {
  const id = (label || lang) + code.slice(0, 20);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', background: '#1e293b', borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid rgba(255,255,255,.08)',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: .5 }}>
          {label || lang}
        </span>
        <CopyBtn text={code} id={id} copied={copied} copy={copy} />
      </div>
      <pre style={{
        margin: 0, padding: '16px 18px',
        background: C.codeDark, borderRadius: '0 0 8px 8px',
        fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
        fontSize: 13, lineHeight: 1.7, color: '#e8e8f1',
        overflowX: 'auto', whiteSpace: 'pre',
      }}>{code}</pre>
    </div>
  );
};

const Inline = ({ children }) => (
  <code style={{
    fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
    fontSize: '0.875em', background: C.codeBg,
    border: `1px solid ${C.border}`,
    borderRadius: 4, padding: '1px 6px', color: C.text,
  }}>{children}</code>
);

const Callout = ({ icon: Icon, title, children, variant = 'info' }) => {
  const v = {
    info:    { bg: C.blueBg,   border: C.blueBorder,   text: C.blue   },
    success: { bg: C.greenBg,  border: C.greenBorder,  text: C.green  },
    warning: { bg: C.yellowBg, border: C.yellowBorder, text: C.yellow },
    danger:  { bg: C.redBg,    border: C.redBorder,    text: C.red    },
  }[variant];
  return (
    <div style={{
      background: v.bg, border: `1px solid ${v.border}`,
      borderRadius: 8, padding: '12px 16px', marginBottom: 20,
      display: 'flex', gap: 10,
    }}>
      {Icon && <Icon size={15} style={{ color: v.text, flexShrink: 0, marginTop: 1 }} />}
      <div>
        {title && <div style={{ fontWeight: 600, fontSize: 13, color: v.text, marginBottom: 3 }}>{title}</div>}
        <div style={{ fontSize: 13, color: v.text, lineHeight: 1.65 }}>{children}</div>
      </div>
    </div>
  );
};

const SectionTitle = ({ id, children, refEl }) => (
  <h2 id={id} ref={refEl} style={{
    fontSize: 20, fontWeight: 700, color: C.text,
    margin: '0 0 6px', paddingTop: 4, scrollMarginTop: 80,
  }}>{children}</h2>
);

const SectionSub = ({ children }) => (
  <p style={{ fontSize: 14, color: C.textSub, margin: '0 0 20px', lineHeight: 1.6 }}>{children}</p>
);

const Divider = () => (
  <hr style={{ border: 'none', borderTop: `1px solid ${C.borderSoft}`, margin: '32px 0' }} />
);

const StepBadge = ({ n }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: '50%',
    background: C.text, color: '#fff', fontSize: 12, fontWeight: 700,
    flexShrink: 0,
  }}>{n}</span>
);

const StepRow = ({ n, title, children }) => (
  <div style={{ display: 'flex', gap: 14, marginBottom: 22 }}>
    <StepBadge n={n} />
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 14, color: C.text, marginBottom: 6, paddingTop: 3 }}>{title}</div>
      {children && <div style={{ fontSize: 13.5, color: C.textSub, lineHeight: 1.7 }}>{children}</div>}
    </div>
  </div>
);

const FieldInput = ({ value, onChange, placeholder, type = 'text', readOnly }) => (
  <input
    type={type} value={value} onChange={onChange}
    placeholder={placeholder} readOnly={readOnly}
    style={{
      width: '100%', padding: '9px 12px',
      border: `1px solid ${C.border}`, borderRadius: 8,
      fontSize: 13, outline: 'none', background: C.bgSub, color: C.text,
      boxSizing: 'border-box',
    }}
  />
);

const Btn = ({ onClick, disabled, loading, children, variant = 'primary', type = 'button' }) => {
  const s = {
    primary:   { bg: C.text,    color: '#fff',   border: C.text        },
    secondary: { bg: C.bgSub,   color: C.text,   border: C.border      },
    success:   { bg: C.greenBg, color: C.green,  border: C.greenBorder },
    danger:    { bg: C.redBg,   color: C.red,    border: C.redBorder   },
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '9px 18px', borderRadius: 8,
      border: `1px solid ${s.border}`,
      background: disabled ? C.bgSub : s.bg,
      color: disabled ? C.textMuted : s.color,
      fontSize: 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all .12s',
    }}>
      {loading ? 'Saving…' : children}
    </button>
  );
};

// ─── Nav sections ─────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'overview',  label: 'Overview'               },
  { id: 'how',       label: 'How it works'            },
  { id: 'createapp', label: '0. Create Shopify App'  },
  { id: 'step1',     label: '1. Connect store'        },
  { id: 'step2',     label: '2. Add script'           },
  { id: 'step3',     label: '3. Add button'           },
  { id: 'step4',     label: '4. Webhook'              },
  { id: 'confirmed', label: '5. Order confirmed page' },
  { id: 'faq',       label: 'FAQ'                     },
];

// ─── Static code snippets ─────────────────────────────────────────────────────
const buttonCode = `<!-- Paste after the Checkout button in your cart file -->
<button
  type="button"
  onclick="A1xPay.initiate()"
  style="display:block;width:100%;margin-top:10px;padding:14px;
         border:none;border-radius:8px;background:#1E293B;
         color:#fff;font-size:15px;font-weight:600;cursor:pointer;">
  Buy It Now
</button>`;

const shopifyCheckoutBtn = `{% comment %}
  ── Shopify default checkout button (commented out) ──
  Uncomment this if you want to show both Shopify + A1xPay buttons.
  Keep it commented to use A1xPay as the only payment method.
{% endcomment %}

{%- comment -%}
<button
  type="submit"
  id="checkout"
  class="cart__checkout-button button"
  name="checkout"
  {% if cart == empty %} disabled {% endif %}
  form="cart-form"
>
  <span class="button-text">{{ 'content.checkout' | t }}</span>
</button>
{%- endcomment -%}

<!-- ✅ A1xPay Payment Button -->
<button
  type="button"
  onclick="A1xPay.initiate()"
  style="display:block;width:100%;margin-top:0;padding:14px;
         border:none;border-radius:8px;background:#1E293B;
         color:#fff;font-size:15px;font-weight:600;cursor:pointer;">
  Buy It Now
</button>`;

const featherCheckoutBtn = `{% comment %}
  ── Feather theme: accelerated-checkout.liquid ──
  Comment out the default accelerated checkout block below,
  then add the Buy It Now button powered by A1xPay.
{% endcomment %}

{%- comment -%}
{% if accelerated_checkout_button != blank %}
  <div
    class="accelerated-checkout-block"
    ref="acceleratedCheckoutButtonContainer"
    {{ block.shopify_attributes }}
    {% if request.visual_preview_mode %}
      data-shopify-visual-preview
    {% endif %}
    {% unless can_add_to_cart %}
      hidden
    {% endunless %}
  >
    {{ accelerated_checkout_button }}
  </div>
{% endif %}
{%- endcomment -%}

<!-- ✅ A1xPay Buy It Now Button -->
<button
  type="button"
  onclick="A1xPay.initiate()"
  style="display:block;width:100%;margin-top:10px;padding:14px;
         border:none;border-radius:8px;background:#1E293B;
         color:#fff;font-size:15px;font-weight:600;cursor:pointer;">
  Buy It Now
</button>`;

const webhookVerify = `<?php
$payload   = file_get_contents('php://input');
$sig       = $_SERVER['HTTP_X_SIGNATURE'] ?? '';
$timestamp = $_SERVER['HTTP_X_TIMESTAMP']  ?? '';

$data      = json_decode($payload, true);
$canonical = "{$data['txn_id']}|{$data['status']}|{$data['amount']}"
           . "|{$data['merchant_order_id']}|{$timestamp}";

$expected  = hash_hmac('sha256', $canonical, getenv('A1XPAY_SECRET_SALT'));

if (!hash_equals($expected, $sig)) {
    http_response_code(401); exit('Invalid signature');
}

if ($data['status'] === 'success') {
    // ✅ Payment confirmed — Shopify order auto-created by A1xPay
}`;

// ─── Main component ───────────────────────────────────────────────────────────
export default function ShopifyIntegration() {
  const [mid,            setMid]            = useState('');
  const [loading,        setLoading]        = useState(true);
  const [shopDomain,     setShopDomain]     = useState('');
  const [clientId,       setClientId]       = useState('');
  const [clientSecret,   setClientSecret]   = useState('');
  const [showSecret,     setShowSecret]     = useState(false);
  const [connecting,     setConnecting]     = useState(false);
  const [disconnecting,  setDisconnecting]  = useState(false);
  const [reconnecting,   setReconnecting]   = useState(false);
  const [step1Done,      setStep1Done]      = useState(false);
  const [connectedStore, setConnectedStore] = useState('');
  const [webhookSaving,  setWebhookSaving]  = useState(false);
  const [step4Done,      setStep4Done]      = useState(false);
  const [activeNav,      setActiveNav]      = useState('overview');
  const { copy, copied } = useClipboard();
  const sectionRefs = useRef({});

  // ── Handle OAuth redirect back (?connected=1 or ?error=xxx) ─────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === '1') {
      const shop = params.get('shop') || '';
      setStep1Done(true);
      setConnectedStore(shop);
      toast.success(`Shopify store connected: ${shop}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error')) {
      const msg = params.get('error').replace(/_/g, ' ');
      toast.error(`Connection failed: ${msg}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ── Load data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    Promise.allSettled([
      merchantCredentialsAPI.show(),
      shopifyConfigAPI.get(),
    ]).then(([credRes, cfgRes]) => {
      if (credRes.status === 'fulfilled') {
        setMid(credRes.value?.data?.data?.merchant_id || '');
      }
      if (cfgRes.status === 'fulfilled') {
        const cfg = cfgRes.value?.data?.data;
        if (cfg?.is_connected || cfg?.shopify_domain) {
          setStep1Done(true);
          setConnectedStore(cfg.shopify_domain || '');
        }
      }
    }).finally(() => setLoading(false));
  }, []);

  // ── Intersection observer for left nav ────────────────────────────────────
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveNav(e.target.id); }),
      { rootMargin: '-25% 0px -65% 0px' }
    );
    Object.values(sectionRefs.current).forEach(el => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [loading]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveNav(id);
  };

  const webhookUrl = mid ? `https://api.a1xpay.com/api/webhook/shopify/${mid}` : '';

  const scriptCode = `<!-- Paste just before </head> in theme.liquid -->
<script>
  window.A1xPayConfig = {
    merchantId: '${mid || 'YOUR_MERCHANT_ID'}',
    mode: 'physical'
  };
</script>
<script
  src="https://api.a1xpay.com/shopify.js"
  onload="A1xPay.init(window.A1xPayConfig)">
</script>`;

  // ── Connect store via Admin API Access Token ──────────────────────────────
  const connectWithCredentials = async () => {
    const domain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '').trim();
    if (!domain)       { toast.error('Enter your store domain.');  return; }
    if (!clientId)     { toast.error('Enter your Client ID.');     return; }
    if (!clientSecret) { toast.error('Enter your Client Secret.'); return; }
    if (!/^[a-zA-Z0-9\-]+\.myshopify\.com$/.test(domain)) {
      toast.error('Domain must be: yourstore.myshopify.com');
      return;
    }
    setConnecting(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const res = await fetch('https://api.a1xpay.com/api/merchant/shopify-connect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type':  'application/json',
          'Accept':        'application/json',
        },
        body: JSON.stringify({
          shop_domain:   domain,
          client_id:     clientId.trim(),
          client_secret: clientSecret.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Connection failed.');
      setStep1Done(true);
      setConnectedStore(domain);
      setReconnecting(false);
      setClientId('');
      setClientSecret('');
      toast.success(`Shopify store connected: ${domain}`);
    } catch (err) {
      toast.error(err.message || 'Could not connect. Check your store domain and access token.');
    } finally {
      setConnecting(false);
    }
  };

  // ── Disconnect store ──────────────────────────────────────────────────────
  const disconnectShopify = async () => {
    if (!window.confirm(`Disconnect ${connectedStore}?\nShopify orders will no longer be auto-created.`)) return;
    setDisconnecting(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      await fetch('https://api.a1xpay.com/api/merchant/shopify-disconnect', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      setStep1Done(false);
      setConnectedStore('');
      setShopDomain('');
      toast.success('Shopify store disconnected.');
    } catch {
      toast.error('Could not disconnect. Try again.');
    } finally { setDisconnecting(false); }
  };

  // ── Save webhook URL ──────────────────────────────────────────────────────
  const saveWebhook = async () => {
    if (!webhookUrl) return;
    setWebhookSaving(true);
    try {
      await merchantCredentialsAPI.updateWebhookUrl(webhookUrl);
      setStep4Done(true);
      toast.success('Webhook URL saved.');
    } catch {
      toast.error('Could not save. Paste manually in API Setup → Webhook URL.');
    } finally { setWebhookSaving(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80, color: C.textMuted, fontSize: 14 }}>
      Loading…
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="doc-layout">

      {/* ── Left sticky nav ─────────────────────────────────────────────── */}
      <nav className="doc-toc" style={{ position: 'sticky', top: 24, alignSelf: 'flex-start' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 12 }}>
          On this page
        </div>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => scrollTo(s.id)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '6px 10px', borderRadius: 6, border: 'none',
            background: activeNav === s.id ? C.bgSub : 'transparent',
            color: activeNav === s.id ? C.text : C.textSub,
            fontWeight: activeNav === s.id ? 600 : 400,
            fontSize: 13, cursor: 'pointer', marginBottom: 1,
            borderLeft: `2px solid ${activeNav === s.id ? C.text : 'transparent'}`,
            transition: 'all .1s',
          }}>{s.label}</button>
        ))}
      </nav>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Page header */}
        <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 6 }}>
            Manage Integrations
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 8px' }}>
            Shopify Setup
          </h1>
          <p style={{ fontSize: 15, color: C.textSub, margin: 0, lineHeight: 1.6 }}>
            Connect your Shopify store to A1xPay. After payment, orders are created automatically
            in Shopify Admin with full customer and shipping details.
          </p>

          {/* Progress badges */}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Store connected',   done: step1Done },
              { label: 'Script added',      done: false     },
              { label: 'Button added',      done: false     },
              { label: 'Webhook saved',     done: step4Done },
              { label: 'Order page created',done: false     },
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 20,
                border: `1px solid ${s.done ? C.greenBorder : C.border}`,
                background: s.done ? C.greenBg : C.bgSub,
              }}>
                {s.done
                  ? <CircleCheck size={12} style={{ color: C.green }} />
                  : <span style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${C.textMuted}`, display: 'inline-block' }} />}
                <span style={{ fontSize: 12, fontWeight: 500, color: s.done ? C.green : C.textSub }}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Overview ────────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="overview" refEl={el => sectionRefs.current['overview'] = el}>
            Overview
          </SectionTitle>
          <SectionSub>
            A1xPay acts as the payment gateway between your Shopify store and the customer.
            This integration is configured for physical products — an address form collects
            delivery details before payment.
          </SectionSub>
          <Callout icon={ChevronRight} title="Physical mode only" variant="info">
            When a customer clicks "Buy It Now", a delivery address form pops up before
            they are redirected to payment. The Shopify order is created with full name, email,
            phone, and shipping address — ready to fulfill.
          </Callout>
        </section>

        <Divider />

        {/* ── How it works ────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="how" refEl={el => sectionRefs.current['how'] = el}>
            How it works
          </SectionTitle>
          <SectionSub>End-to-end flow from cart to fulfilled Shopify order.</SectionSub>

          <div style={{ marginBottom: 24 }}>
            {[
              'Customer clicks Buy It Now — items added to cart automatically if needed.',
              'Address form pops up — name, email, phone, full address.',
              'A1xPay creates a transaction with cart data and customer info.',
              'Customer redirected to PayU checkout — pays.',
              'A1xPay confirms payment and credits merchant wallet.',
              'Shopify order auto-created — marked Paid with shipping address.',
              'Merchant ships from Shopify Admin → marks Fulfilled.',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: i === 5 ? C.green : C.text,
                  color: '#fff', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</div>
                <div style={{ paddingTop: 3, fontSize: 14, color: C.textSub, lineHeight: 1.5 }}>
                  {text}
                </div>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── Step 0: Create Shopify App ───────────────────────────────────── */}
        <section>
          <SectionTitle id="createapp" refEl={el => sectionRefs.current['createapp'] = el}>
            0. Create a Shopify App
          </SectionTitle>
          <SectionSub>
            Before connecting, you need a custom app from Shopify Partners Dashboard.
            This gives you the Client ID and Client Secret required in the next step.
          </SectionSub>

          <Callout icon={ChevronRight} variant="info" title="One-time setup">
            You only need to do this once per store. The app stays connected permanently
            unless you revoke it from Shopify.
          </Callout>

          {/* Steps to create app */}
          <StepRow n="1" title={<span>Go to <strong>partners.shopify.com</strong> and log in</span>}>
            Create a free Shopify Partners account if you don't have one.
          </StepRow>

          <StepRow n="2" title='Click "Apps" in the left sidebar → then "Create app"'>
            Choose <strong>"Create app manually"</strong> when prompted.
          </StepRow>

          <StepRow n="3" title="Fill in app details">
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '6px 16px', fontSize: 13, marginTop: 4 }}>
              <span style={{ color: C.textMuted, fontWeight: 600 }}>App name</span>
              <span>Anything — e.g. <Inline>A1xPay Integration</Inline></span>
              <span style={{ color: C.textMuted, fontWeight: 600 }}>App URL</span>
              <span><Inline>https://api.a1xpay.com</Inline></span>
              <span style={{ color: C.textMuted, fontWeight: 600 }}>Redirect URL</span>
              <span><Inline>https://api.a1xpay.com/api/shopify/oauth/callback</Inline></span>
            </div>
          </StepRow>

          <StepRow n="4" title='Go to "API credentials" tab → copy Client ID and Client Secret' />

          <StepRow n="5" title='Click "Configuration" tab → under "Admin API integration" select these scopes:' />

          {/* Scopes table */}
          <div style={{
            border: `1px solid ${C.border}`, borderRadius: 10,
            overflow: 'hidden', marginBottom: 20,
          }}>
            {/* Header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              background: C.text, padding: '10px 16px',
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: .6 }}>Scope</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .6 }}>Why it's needed</span>
            </div>

            {[
              { scope: 'write_orders',    why: 'Create paid order after payment',         required: true  },
              { scope: 'read_orders',     why: 'Read order status',                        required: true  },
              { scope: 'read_products',   why: 'Verify cart item prices & variants',       required: true  },
              { scope: 'write_customers', why: 'Create customer record with order',        required: true  },
              { scope: 'read_customers',  why: 'Check if customer already exists',         required: true  },
              { scope: 'read_inventory',  why: 'Check product stock (optional)',           required: false },
            ].map((row, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                padding: '10px 16px',
                background: i % 2 === 0 ? C.bg : C.bgSub,
                borderTop: `1px solid ${C.borderSoft}`,
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                    background: row.required ? C.greenBg : C.blueBg,
                    color: row.required ? C.green : C.blue,
                    border: `1px solid ${row.required ? C.greenBorder : C.blueBorder}`,
                    flexShrink: 0,
                  }}>
                    {row.required ? 'Required' : 'Optional'}
                  </span>
                  <code style={{
                    fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
                    fontSize: 12, color: C.text,
                  }}>{row.scope}</code>
                </div>
                <span style={{ fontSize: 13, color: C.textSub }}>{row.why}</span>
              </div>
            ))}
          </div>

          <StepRow n="6" title='Click "Save" then "Install app" on your store' />

          <Callout icon={CircleCheck} variant="success" title="After installing">
            Go to <strong>API credentials</strong> tab — you will see your <strong>Client ID</strong> and <strong>Client Secret</strong>.
            Copy both and paste them in <strong>Step 1 below</strong> to connect your store to A1xPay.
          </Callout>
        </section>

        <Divider />

        {/* ── Step 1: Connect store ────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step1" refEl={el => sectionRefs.current['step1'] = el}>
            1. Connect your Shopify store
          </SectionTitle>
          <SectionSub>
            Enter your Shopify app's Client ID and Client Secret from the Shopify Partners Dashboard.
            A1xPay will fetch and save the access token automatically — one-time setup.
          </SectionSub>

          {/* Already connected card */}
          {step1Done && !reconnecting ? (
            <div style={{
              border: `1px solid ${C.greenBorder}`, background: C.greenBg,
              borderRadius: 14, padding: '20px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 16, marginBottom: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%', background: C.green,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ShoppingBag size={19} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 2 }}>
                    Shopify Connected ✓
                  </div>
                  <div style={{
                    fontSize: 13, color: C.green, opacity: .8,
                    fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
                  }}>
                    {connectedStore}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setReconnecting(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${C.greenBorder}`,
                    background: C.bg, color: C.green,
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  <RefreshCw size={13} /> Reconnect
                </button>
                <button
                  onClick={disconnectShopify}
                  disabled={disconnecting}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${C.redBorder}`,
                    background: C.redBg, color: C.red,
                    fontSize: 13, fontWeight: 500,
                    cursor: disconnecting ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Link2Off size={13} /> {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : null}

          {/* Credential connect form — shown when not connected OR reconnecting */}
          {(!step1Done || reconnecting) && (
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: 14,
              padding: 24, background: C.bgSub, marginBottom: 20,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                Enter your Shopify app credentials
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 20, lineHeight: 1.6 }}>
                Go to <strong>partners.shopify.com</strong> → Apps → Your App → API credentials
              </div>

              {/* Domain */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6, textTransform: 'uppercase', letterSpacing: .4 }}>
                  Store Domain
                </div>
                <FieldInput
                  value={shopDomain}
                  onChange={e => setShopDomain(e.target.value)}
                  placeholder="yourstore.myshopify.com"
                />
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                  Must end with .myshopify.com — even if you have a custom domain (e.g. trolin.in), enter your original Shopify domain. Find it in: Shopify Admin → Settings → Domains
                </div>
              </div>

              {/* Client ID */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6, textTransform: 'uppercase', letterSpacing: .4 }}>
                  Client ID
                </div>
                <FieldInput
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                  placeholder="cbd7fd2231ebf6c8a73532ae1caa8f77"
                />
              </div>

              {/* Client Secret */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6, textTransform: 'uppercase', letterSpacing: .4 }}>
                  Client Secret
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)}
                    placeholder="shpss_••••••••••••••••••••••••••••••••"
                    style={{
                      width: '100%', padding: '9px 40px 9px 12px',
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      fontSize: 13, outline: 'none', background: C.bg, color: C.text,
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(p => !p)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 0,
                    }}
                  >
                    {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  onClick={connectWithCredentials}
                  disabled={connecting || !shopDomain.trim() || !clientId.trim() || !clientSecret.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 22px', borderRadius: 8, border: 'none',
                    background: (connecting || !shopDomain.trim() || !clientId.trim() || !clientSecret.trim())
                      ? C.bgSub : C.text,
                    color: (connecting || !shopDomain.trim() || !clientId.trim() || !clientSecret.trim())
                      ? C.textMuted : '#fff',
                    fontSize: 14, fontWeight: 600,
                    cursor: (connecting || !shopDomain.trim() || !clientId.trim() || !clientSecret.trim())
                      ? 'not-allowed' : 'pointer',
                    transition: 'all .15s',
                  }}
                >
                  {connecting
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Connecting…</>
                    : <><Plug size={14} /> Connect Store</>
                  }
                </button>
                {reconnecting && (
                  <button
                    onClick={() => setReconnecting(false)}
                    style={{
                      padding: '10px 16px', borderRadius: 8,
                      border: `1px solid ${C.border}`, background: C.bg,
                      color: C.textSub, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          <Callout icon={ChevronRight} title="Where to find Client ID & Secret?" variant="info">
            <strong>partners.shopify.com</strong> → Apps → Select your app → <strong>API credentials</strong> tab →
            copy <em>Client ID</em> and <em>Client secret</em>. Token is fetched and saved automatically — never stored in browser.
          </Callout>
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </section>

        <Divider />

        {/* ── Step 2: Script ───────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step2" refEl={el => sectionRefs.current['step2'] = el}>
            2. Add the A1xPay script
          </SectionTitle>
          <SectionSub>
            Loads the payment script on every page of your Shopify store.
          </SectionSub>

          <StepRow n="1" title="Open theme.liquid">
            <strong>Shopify Admin → Online Store → Themes → Edit Code → Layout → theme.liquid</strong>
          </StepRow>
          <StepRow n="2" title={<span>Find the closing <Inline>&lt;/head&gt;</Inline> tag and paste just before it:</span>} />

          <CodeBlock code={scriptCode} lang="liquid" label="theme.liquid" copied={copied} copy={copy} />

          {mid && (
            <Callout icon={CircleCheck} variant="success">
              Your Merchant ID <Inline>{mid}</Inline> is already filled in the script above.
            </Callout>
          )}

          <StepRow n="3" title="Click Save in the Shopify editor." />
        </section>

        <Divider />

        {/* ── Step 3: Button ───────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step3" refEl={el => sectionRefs.current['step3'] = el}>
            3. Add the Buy It Now button
          </SectionTitle>
          <SectionSub>
            Find the Checkout button in your cart template and replace or add alongside it.
          </SectionSub>

          {/* Where to find the file */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            {[
              {
                title: 'Horizon theme',
                file:  'Snippets → cart-summary.liquid',
                note:  'Covers both the cart page and cart drawer.',
              },
              {
                title: 'Feather theme',
                file:  'Blocks → accelerated-checkout.liquid',
                note:  'Comment out the accelerated checkout block and add the Buy It Now button.',
              },
              {
                title: 'Other themes (Dawn, Debut…)',
                file:  'Sections → main-cart-footer.liquid\nSections → cart-drawer.liquid',
                note:  'Add to both files if you have a cart drawer.',
              },
            ].map((t, i) => (
              <div key={i} style={{
                border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '14px 16px', background: C.bgSub,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>{t.title}</div>
                <div style={{ fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace', fontSize: 12, color: C.blue, whiteSpace: 'pre-line', marginBottom: 6 }}>{t.file}</div>
                <div style={{ fontSize: 12, color: C.textMuted }}>{t.note}</div>
              </div>
            ))}
          </div>

          {/* Feather theme accelerated-checkout.liquid */}
          <CodeBlock
            code={featherCheckoutBtn}
            lang="liquid"
            label="accelerated-checkout.liquid — Feather theme"
            copied={copied}
            copy={copy}
          />

          {/* Option A */}
          <div style={{
            border: `1px solid ${C.greenBorder}`, borderRadius: 10,
            padding: '14px 18px', background: C.greenBg, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>
                Option A — Only A1xPay (recommended)
              </span>
            </div>
            <div style={{ fontSize: 13, color: C.green, lineHeight: 1.6 }}>
              Comment out the Shopify Checkout button and replace with the A1xPay button.
              Customers will <strong>only</strong> see "Buy It Now" — no Shopify checkout.
            </div>
          </div>

          <CodeBlock
            code={shopifyCheckoutBtn}
            lang="liquid"
            label="cart-summary.liquid — Only A1xPay"
            copied={copied}
            copy={copy}
          />

          {/* Option B */}
          <div style={{
            border: `1px solid ${C.blueBorder}`, borderRadius: 10,
            padding: '14px 18px', background: C.blueBg, marginBottom: 16, marginTop: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>➕</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.blue }}>
                Option B — Both buttons (Shopify + A1xPay)
              </span>
            </div>
            <div style={{ fontSize: 13, color: C.blue, lineHeight: 1.6 }}>
              Keep the existing Checkout button and paste the A1xPay button <strong>after</strong> it.
              Customers can choose either payment method.
            </div>
          </div>

          <CodeBlock
            code={buttonCode}
            lang="liquid"
            label="cart-summary.liquid — Add after Checkout button"
            copied={copied}
            copy={copy}
          />

          <Callout icon={ChevronRight} variant="info">
            The button reads the live cart total and items from Shopify automatically — no amount hardcoding needed.
          </Callout>
        </section>

        <Divider />

        {/* ── Step 4: Webhook ──────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step4" refEl={el => sectionRefs.current['step4'] = el}>
            4. Save your webhook URL
          </SectionTitle>
          <SectionSub>
            This URL receives payment confirmations and triggers automatic Shopify order creation.
          </SectionSub>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', border: `1px solid ${C.border}`,
            borderRadius: 8, background: C.bgSub, marginBottom: 14, gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{
              fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
              fontSize: 13, color: C.text, wordBreak: 'break-all',
            }}>
              {webhookUrl || <span style={{ color: C.textMuted }}>Loading…</span>}
            </span>
            {webhookUrl && (
              <button
                onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Copied!'); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '5px 12px', border: `1px solid ${C.border}`,
                  borderRadius: 6, background: C.bg, color: C.textSub,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                <Copy size={12} /> Copy
              </button>
            )}
          </div>

          {step4Done ? (
            <Callout icon={CircleCheck} variant="success">
              Webhook URL saved. Shopify orders will be created automatically after every successful payment.
            </Callout>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Btn onClick={saveWebhook} loading={webhookSaving} disabled={webhookSaving || !webhookUrl}>
                <Webhook size={13} /> Save automatically
              </Btn>
              <span style={{ fontSize: 12, color: C.textMuted }}>
                or paste manually in API Setup → Webhook URL
              </span>
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
              Verify webhook signatures (recommended)
            </div>
            <CodeBlock code={webhookVerify} lang="php" copied={copied} copy={copy} />
          </div>
        </section>

        <Divider />

        {/* ── Order confirmed page ─────────────────────────────────────────── */}
        <section>
          <SectionTitle id="confirmed" refEl={el => sectionRefs.current['confirmed'] = el}>
            5. Create Order Confirmed page
          </SectionTitle>
          <SectionSub>
            After payment, customers are redirected to this page. You must create it in Shopify — otherwise customers see "Page not found".
          </SectionSub>

          <Callout icon={ChevronRight} title="Required step" variant="warning">
            Without this page, customers will see a <strong>"Page not found"</strong> error after successful payment.
            The order is still created — only the redirect destination is missing.
          </Callout>

          <StepRow n="1" title={<span>Go to <strong>Shopify Admin → Online Store → Pages → Add page</strong></span>} />

          <StepRow n="2" title="Fill in the page details">
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 16px', fontSize: 13, marginTop: 4 }}>
              <span style={{ color: C.textMuted, fontWeight: 600 }}>Title</span>
              <span><Inline>Order Confirmed</Inline></span>
              <span style={{ color: C.textMuted, fontWeight: 600 }}>URL handle</span>
              <span><Inline>order-confirmed</Inline> <span style={{ color: C.textMuted }}>(auto-set)</span></span>
              <span style={{ color: C.textMuted, fontWeight: 600 }}>Visibility</span>
              <span><strong>Visible</strong></span>
            </div>
          </StepRow>

          <StepRow n="3" title="In the content editor, paste the following text (no HTML needed):" />

          {/* Page Title */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 8 }}>
              Page Title
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', background: C.codeDark, borderRadius: 8,
            }}>
              <span style={{ fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace', fontSize: 13, color: '#e8e8f1' }}>
                Order Confirmed
              </span>
              <CopyBtn text="Order Confirmed" id="oc-title" copied={copied} copy={copy} />
            </div>
          </div>

          {/* Page Content */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 8 }}>
              Page Content
            </div>
            <div style={{
              background: C.codeDark, borderRadius: 8, overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,.08)',
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: .5 }}>content</span>
                <CopyBtn
                  text={`Thank you for your purchase!\n\nYour order has been successfully placed and payment has been received.\n\nWhat happens next?\n\nOur team will process your order within 1–2 business days. Once your order is shipped, you will receive a tracking update on your registered email address.\n\nNeed help?\n\nIf you have any questions about your order, feel free to reach us through the Contact Us page or reply to your order confirmation email.\n\nWe appreciate your trust and hope you love your purchase!`}
                  id="oc-content"
                  copied={copied}
                  copy={copy}
                />
              </div>
              <pre style={{
                margin: 0, padding: '16px 18px',
                fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
                fontSize: 13, lineHeight: 1.8, color: '#e8e8f1',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>{`Thank you for your purchase!

Your order has been successfully placed and payment has been received.

What happens next?

Our team will process your order within 1–2 business days. Once your order is shipped, you will receive a tracking update on your registered email address.

Need help?

If you have any questions about your order, feel free to reach us through the Contact Us page or reply to your order confirmation email.

We appreciate your trust and hope you love your purchase!`}</pre>
            </div>
          </div>

          <StepRow n="4" title='Set Visibility to "Visible" and click Save.' />

          <StepRow n="5" title="After saving, customers will land here after payment:">
            <CodeBlock
              code={`https://${connectedStore || 'yourstore.myshopify.com'}/pages/order-confirmed?status=success&txn_id=STXN_...`}
              lang=""
              label="redirect URL"
              copied={copied}
              copy={copy}
            />
          </StepRow>

          <Callout icon={CircleCheck} variant="success" title="What customers see">
            A clean confirmation page with order success message, next steps, and support info — ready for any store.
          </Callout>
        </section>

        <Divider />

        {/* ── FAQ ──────────────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="faq" refEl={el => sectionRefs.current['faq'] = el}>
            FAQ
          </SectionTitle>
          <SectionSub>Common questions about the Shopify integration.</SectionSub>

          {[
            {
              q: 'Do I need to manually mark orders as Paid in Shopify?',
              a: 'No. Once your Shopify API token is connected, A1xPay creates orders automatically as Paid after every successful payment. You only need to fulfill (ship) the order.',
            },
            {
              q: 'What happens if I don\'t connect the Shopify API token?',
              a: 'Payment still works and money reaches your A1xPay wallet, but no Shopify order is created automatically. You would need to create the order manually.',
            },
            {
              q: 'Will customers get an order confirmation email?',
              a: 'Yes. Shopify automatically sends an order confirmation email to the customer\'s email address (collected in the address form) when the order is created.',
            },
            {
              q: 'Is my Admin API token safe?',
              a: 'Yes. The token is stored securely in A1xPay\'s encrypted database. It is only used server-side to create orders — it is never sent to the browser or any third party.',
            },
            {
              q: 'Which Shopify themes are supported?',
              a: 'All themes. Horizon uses cart-summary.liquid in Snippets. Other themes (Dawn, Debut, etc.) use main-cart-footer.liquid and cart-drawer.liquid in Sections.',
            },
          ].map(({ q, a }, i, arr) => (
            <div key={i} style={{
              marginBottom: 20, paddingBottom: 20,
              borderBottom: i < arr.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>{q}</div>
              <div style={{ fontSize: 13.5, color: C.textSub, lineHeight: 1.7 }}>{a}</div>
            </div>
          ))}
        </section>

        <div style={{ height: 64 }} />
        <style>{`
          .doc-layout { max-width: 1100px; margin: 0 auto; display: flex; gap: 48px; align-items: flex-start; }
          .doc-toc { width: 200px; flex-shrink: 0; }
          @media (max-width: 768px) { .doc-toc { display: none; } }
        `}</style>
      </div>
    </div>
  );
}
